import { useLoaderData } from "@remix-run/react";
import { LoaderFunction, redirect } from "@remix-run/server-runtime";
import { useShallowCompareEffect } from "react-use";
import invariant from "tiny-invariant";
import { ClientSide } from "~/@types";
import {
  IInsight,
  INewsletter,
  SpecificLocale,
} from "~/@types/generated/contentful";
import EmbargoedWatermark from "~/components/Insight/EmbargoedWatermarking";
import InsightComponent from "~/components/Insight/Insight";
import InsightHero from "~/components/heros/InsightHero";
import { Newsletter } from "~/components/shared";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import SchemaOrg from "~/components/utils/SchemaOrg";
import { getFrontifyImageURL } from "~/components/utils/getFrontifyImage";
import { getTwicPicsURL } from "~/components/utils/getTwicPicsURL";
import { pageView, track } from "~/components/utils/gtm.client";
import {
  getExpertSchema,
  SchemaOrgType,
  tbiOrganizationSchema,
} from "~/components/utils/schemaOrgUtils.server";
import { getEntryBySlug, getNewsletter } from "~/contentful/index.server";
import * as routeUtils from "~/contentful/route-utils";
import {
  ComponentDataProvider,
  getInsightComponentData,
  InsightComponentData,
} from "~/contexts/ComponentDataProvider";
import { i18n } from "~/i18n.server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("insight", "insights");

interface LoaderData {
  insight: ClientSide<IInsight>;
  newsletter: ClientSide<INewsletter>;
  componentData: InsightComponentData;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.insightSlug;

  invariant(slugToQuery, invariantMessage);

  const insight = (await getEntryBySlug("insight", slugToQuery, {
    locale,
  })) as SpecificLocale<IInsight>;

  if (!insight) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Insight Not Found",
        backgroundColor: "white",
        notFound: "insight",
      } as FourOFourCaughtData),
      { status: 404 }
    );
  }

  const newsletter = await getNewsletter(locale);

  const componentData = await getInsightComponentData({
    insight,
    locale,
    params,
  });

  if (insight.fields.topic?.fields.slug !== params.topicSlug) {
    return redirect(
      `/insights/${insight.fields.topic?.fields.slug}/${insight.fields.slug}`,
      301
    );
  }

  const schemaUrl = new URL(request.url);
  schemaUrl.search = "";
  schemaUrl.hash = "";
  const schemaOrg: SchemaOrgType = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": schemaUrl.toString(),
    headline: insight.fields.title,
    keywords: insight.fields.tags?.map((t) => t?.fields?.title).filter(Boolean),
    author: insight.fields.experts?.map(getExpertSchema),
    datePublished: insight.fields.date
      ? new Date(insight.fields.date).toISOString()
      : new Date().toISOString(),
    publisher: tbiOrganizationSchema,
    sourceOrganization: tbiOrganizationSchema,
    image: insight.fields?.image?.fields?.image?.fields?.file?.url,
    url: schemaUrl.toString(),
    isAccessibleForFree: true,
    embargoed: insight.fields?.embargoed,
  };

  const openGraphImageUrl = insight.fields.image?.fields
    ? insight.fields.image?.fields.frontifyImage
      ? getFrontifyImageURL(insight.fields.image.fields, 1200)
      : getTwicPicsURL(insight.fields.image.fields, "&w=1200")
    : "";

  const meta = routeUtils.getMetaInfo({
    title: insight.fields.title,
    description: insight.fields.description ?? insight.fields.title,
    requestUrl: request.url,
    openGraphImageUrl,
    openGraphType: "insight",
  });

  if (insight.fields.embargoed) {
    meta.robots = "noindex, nofollow";
  }

  return {
    insight,
    newsletter,
    componentData,
    meta: meta,
    schemaOrg,
  } as LoaderData;
};

export default function InsightPage() {
  const { newsletter, componentData, schemaOrg } = useLoaderData<LoaderData>();
  const { insight } = componentData;

  useShallowCompareEffect(() => {
    track(
      pageView({
        category: "Article",
        author:
          insight.fields.experts?.map(
            (e) => `${e.fields.firstName} ${e.fields.lastName}`
          ) ?? "",
        topic: insight.fields.topic?.fields.title ?? "",
        publishDate: insight.fields.date,
        articleTitle: insight.fields.title,
      })
    );
  }, [insight.fields]);

  return (
    <ComponentDataProvider componentData={componentData}>
      {insight.fields.embargoed && <EmbargoedWatermark />}
      <SchemaOrg content={schemaOrg} />
      <InsightHero />
      <InsightComponent insight={insight} url={schemaOrg.url} />
      {newsletter?.fields && <Newsletter fields={newsletter.fields} />}
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const CatchBoundary = routeUtils.ContentfulCatchBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
